import React, { useMemo, useRef, ReactNode, useEffect, MutableRefObject } from 'react';
import Flickity, { FlickityEventName, FlickityOptions } from 'react-flickity-component';

import 'flickity/dist/flickity.min.css';
import styles from './Carousel.module.scss';
import classNames from 'classnames';

interface CarouselProps {
  cellAlign?: 'left' | 'center' | 'right';
  children: ReactNode[];
  onChange?(index: number): void;
  onSettle?(index: number): void;
  className?: string;
  initialIndex?: number;
  controllerRef?: MutableRefObject<CarouselController | undefined>;
  pageDots?: boolean;
  prevNextButtons?: boolean;
  friction?: number;
  wrapAround?: boolean;
  freeScroll?: boolean;
}

function useFlickityListener(
  ref: React.MutableRefObject<Flickity | undefined>,
  eventName: FlickityEventName,
  callback: ((...args: any[]) => void) | undefined,
) {
  useEffect(() => {
    let listener: ((...args: any[]) => void) | undefined;
    const flickity = (ref.current as any)?.flkty as Flickity | null;
    if (callback && flickity) {
      listener = (...args: any[]) => callback(...args);
      flickity.on(eventName, listener);
    }
    return () => {
      if (listener && flickity) {
        flickity.off(eventName, listener);
      }
    };
  }, [ref, eventName, callback]);
}

export interface CarouselController {
  select(index: number, instant?: boolean): void;
}

export function Carousel({
  cellAlign,
  children,
  className,
  onChange,
  onSettle,
  initialIndex,
  controllerRef,
  pageDots,
  prevNextButtons,
  friction,
  wrapAround,
  freeScroll,
}: CarouselProps) {
  // Note: actual flickity instance is flickityRef.current.flkty
  const flickityRef = useRef<Flickity>();

  const flickityOptions = useMemo(
    (): FlickityOptions => ({
      prevNextButtons: prevNextButtons ?? false,
      pageDots: pageDots ?? false,
      cellAlign: cellAlign ?? 'left',
      friction: friction ?? 0.28,
      initialIndex,
      // https://github.com/metafizzy/flickity/issues/366#issuecomment-213642947
      setGallerySize: false,
      wrapAround: wrapAround ?? false,
      freeScroll: freeScroll,
    }),
    [cellAlign, initialIndex, pageDots, prevNextButtons, friction, wrapAround, freeScroll],
  );

  useFlickityListener(flickityRef, 'change', onChange);
  useFlickityListener(flickityRef, 'settle', onSettle);

  /**
   * Exposes a CarouselController to the parent via controllerRef, allowing
   * external code (e.g. thumbnail clicks) to programmatically change slides.
   *
   * The Flickity instance is looked up fresh inside select() rather than
   * captured at effect time, because react-flickity-component may recreate
   * the underlying Flickity instance on re-render. A stale closure over
   * the old instance would silently no-op.
   */
  useEffect(() => {
    if (controllerRef) {
      controllerRef.current = {
        select(index, instant) {
          const flickity = (flickityRef.current as any)?.flkty as Flickity | null;
          if (!flickity) return;
          flickity.select(index, false, instant);
          if (instant) {
            flickity.reloadCells();
          }
        },
      };
    }
  }, [flickityRef, controllerRef]);

  /**
   * Re-measure once the cell images finish loading. Flickity is created with
   * `disableImagesLoaded`, so it measures cell widths at init — before the
   * images have intrinsic dimensions. For `wrapAround` carousels that leaves
   * the slider parked off-screen (a blank frame on first load) with no
   * re-layout when the images finally arrive. Resizing after each image loads
   * re-measures and re-centers the selected cell. `resize()` is idempotent, so
   * this is a no-op for carousels already sized correctly (e.g. cells with
   * explicit dimensions), keeping it safe for every call site.
   */
  const childCount = React.Children.count(children);
  useEffect(() => {
    let cancelled = false;
    const cleanups: Array<() => void> = [];

    const attach = (attempt: number) => {
      if (cancelled) return;
      // react-flickity-component may not have created the instance in the same
      // tick this effect runs; retry for a few frames before giving up.
      const flickity = (flickityRef.current as any)?.flkty;
      if (!flickity) {
        if (attempt < 10) requestAnimationFrame(() => attach(attempt + 1));
        return;
      }

      const resize = () => {
        if (cancelled) return;
        try {
          flickity.resize();
        } catch {
          /* instance may have been destroyed on unmount */
        }
      };

      const root: HTMLElement | undefined = flickity.element;
      const imgs = root ? Array.from(root.querySelectorAll('img')) : [];
      const pending = imgs.filter((img) => !img.complete || img.naturalWidth === 0);

      if (pending.length === 0) {
        // Images already cached, but init may still have mis-measured — nudge once.
        requestAnimationFrame(resize);
        return;
      }

      pending.forEach((img) => {
        img.addEventListener('load', resize, { once: true });
        img.addEventListener('error', resize, { once: true });
        cleanups.push(() => {
          img.removeEventListener('load', resize);
          img.removeEventListener('error', resize);
        });
      });
    };

    const attachVisibilityResize = (attempt: number) => {
      if (cancelled) return;
      const flickity = (flickityRef.current as any)?.flkty;
      if (!flickity) {
        if (attempt < 10) requestAnimationFrame(() => attachVisibilityResize(attempt + 1));
        return;
      }
      const root: HTMLElement | undefined = flickity.element;
      if (!root || typeof IntersectionObserver === 'undefined') return;
      // A carousel mounted below the fold can mis-measure its cells (e.g. against a
      // transient layout width), and with `wrapAround` that parks the slider off-screen
      // until the user swipes. Re-measure once when it first scrolls into view.
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          try {
            flickity.resize();
          } catch {
            /* instance may have been destroyed on unmount */
          }
          observer.disconnect();
        }
      });
      observer.observe(root);
      cleanups.push(() => observer.disconnect());
    };

    attach(0);
    attachVisibilityResize(0);

    return () => {
      cancelled = true;
      cleanups.forEach((fn) => fn());
    };
  }, [childCount]);

  return (
    <Flickity
      className={classNames(styles.main, className)}
      options={flickityOptions}
      ref={(f) => {
        flickityRef.current = f || undefined;
      }}
      disableImagesLoaded
    >
      {children}
    </Flickity>
  );
}
